/**
 * Proposal Templates
 *
 * Built-in templates the user picks from in Step 5 of the bid wizard.
 * Each template body uses {{placeholder}} tokens that get filled in at
 * preview/PDF time. Users can edit the body inline for the current bid;
 * edits are not persisted back to the template.
 *
 * Supported placeholders:
 *   {{client_name}}        — Client / customer name
 *   {{engagement_name}}    — Engagement / project title
 *   {{bid_amount}}         — Formatted total bid (e.g. $125,000)
 *   {{bid_amount_label}}   — "total project fee" or "monthly retainer"
 *   {{services}}           — Bulleted list of service categories
 *   {{date}}               — Today's date (e.g. May 14, 2026)
 *   {{firm_name}}          — Firm / agency name (user-editable)
 *   {{validity_days}}      — Number of days the proposal is valid (default 30)
 */

export const PROPOSAL_TEMPLATES = [
  {
    id: 'formal-project',
    name: 'Formal Project Proposal',
    description: 'Traditional, detailed proposal for fixed-scope projects.',
    body: `PROPOSAL FOR {{client_name}}

Date: {{date}}
Engagement: {{engagement_name}}
Prepared by: {{firm_name}}

Dear {{client_name}},

Thank you for the opportunity to submit this proposal. Based on our discussions and understanding of your requirements, we are pleased to outline the following engagement.

SCOPE OF SERVICES
We will deliver the following services:
{{services}}

INVESTMENT
The total project fee for this engagement is {{bid_amount}}. This fee covers all of the services outlined above and includes project management, deliverables, and standard revisions.

TIMELINE & TERMS
Work will commence upon countersignature of this proposal. This proposal is valid for {{validity_days}} days from the date above.

We look forward to partnering with you on this engagement.

Sincerely,
{{firm_name}}`,
  },
  {
    id: 'retainer-monthly',
    name: 'Monthly Retainer Agreement',
    description: 'Recurring engagement with a monthly fee.',
    body: `RETAINER PROPOSAL — {{client_name}}

Date: {{date}}
Engagement: {{engagement_name}}
Prepared by: {{firm_name}}

Hello {{client_name}},

We're excited to propose an ongoing retainer relationship to support your team's needs. The following outlines the scope and investment for this engagement.

MONTHLY SCOPE
Each month, our team will provide:
{{services}}

MONTHLY INVESTMENT
The monthly retainer is {{bid_amount}}, billed in advance on the first of each month. The retainer covers the recurring services described above; out-of-scope work will be quoted separately.

TERM
This agreement begins on the date of countersignature and continues month-to-month. Either party may terminate with 30 days' written notice. Proposal valid for {{validity_days}} days.

Thank you for considering us as a partner.

Warm regards,
{{firm_name}}`,
  },
  {
    id: 'concise',
    name: 'Concise / Letter Style',
    description: 'Short, friendly one-pager. Good for repeat clients.',
    body: `Hi {{client_name}},

Per our conversation, here is the proposal for {{engagement_name}}.

What we'll deliver:
{{services}}

Investment: {{bid_amount}} ({{bid_amount_label}}).

Once approved, we'll send a kickoff calendar invite and the first invoice. This proposal is valid for {{validity_days}} days.

Let me know if you'd like to adjust anything.

Best,
{{firm_name}}
{{date}}`,
  },
  {
    id: 'detailed-scope',
    name: 'Detailed Scope & Deliverables',
    description: 'Comprehensive template with extra sections for assumptions and exclusions.',
    body: `PROJECT PROPOSAL — {{engagement_name}}

Prepared for: {{client_name}}
Prepared by: {{firm_name}}
Date: {{date}}

EXECUTIVE SUMMARY
This proposal outlines the scope, deliverables, investment, and terms for {{engagement_name}}. Our team is well-positioned to deliver the outcomes described below and looks forward to your partnership.

DETAILED SCOPE
The engagement includes the following workstreams:
{{services}}

ASSUMPTIONS
- Client will provide timely access to stakeholders, source materials, and decision-makers.
- Deliverables are based on the scope described above; material changes will be handled via a change order.
- Standard business hours apply unless otherwise agreed.

EXCLUSIONS
- Third-party software, licensing, or media costs.
- Work outside the scope of services listed above.

INVESTMENT
Total: {{bid_amount}} ({{bid_amount_label}}).

ACCEPTANCE
This proposal is valid for {{validity_days}} days. To proceed, please countersign and return.

Respectfully submitted,
{{firm_name}}`,
  },
];

export const DEFAULT_PROPOSAL_VARIABLES = {
  firm_name:    'ScaleTime',
  validity_days: 30,
};
